import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateSr } from "@normax-performance/shared-format";
import { DateField } from "../../../shared-ui/primitives";
import { computeReportPeriod, type ReportPeriodRange, type ResolvableReportPeriodPresetKey } from "../period";

export interface ReportPeriodControlProps {
  period: ReportPeriodRange;
  onChange: (period: ReportPeriodRange) => void;
  onLastRecordedDay?: () => void;
  /** When true (DAILY_PRODUCTION's backend-enforced `from === to`, task
   * spec §7), only single-day presets and a single-date custom input are
   * offered — the multi-day presets (thisWeek/previousWeek/thisMonth/
   * previousMonth/thisYear) would otherwise reach the backend and 400 on an
   * ordinary click. */
  singleDayOnly?: boolean;
}

const RESOLVABLE_PRESETS: ResolvableReportPeriodPresetKey[] = ["yesterday", "thisWeek", "previousWeek", "thisMonth", "previousMonth", "thisYear"];
const SINGLE_DAY_PRESETS: ResolvableReportPeriodPresetKey[] = ["yesterday"];

/** Period preset pills + custom range — same recap-string pattern as
 * Analytics' `PeriodControl`, extended with the extra presets task spec §6
 * requires plus the "Poslednji evidentirani dan" action (§7, §9). */
export function ReportPeriodControl({ period, onChange, onLastRecordedDay, singleDayOnly = false }: ReportPeriodControlProps) {
  const { t } = useTranslation("reports");
  const [draftFrom, setDraftFrom] = useState(period.from);
  const [draftTo, setDraftTo] = useState(period.to);
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    setDraftFrom(period.from);
    setDraftTo(period.to);
  }, [period.from, period.to]);

  const presets = singleDayOnly ? SINGLE_DAY_PRESETS : RESOLVABLE_PRESETS;

  return (
    <div className="nx-report-period-control" role="group" aria-label={t("period.presets.custom")}>
      <div className="nx-report-period-control__presets">
        {presets.map((preset) => (
          <button key={preset} type="button" className="nx-report-period-control__preset" onClick={() => onChange(computeReportPeriod(preset, new Date()))}>
            {t(`period.presets.${preset}`)}
          </button>
        ))}
        {onLastRecordedDay ? (
          <button type="button" className="nx-report-period-control__preset" onClick={onLastRecordedDay}>
            {t("period.presets.lastRecordedDay")}
          </button>
        ) : null}
        <button type="button" className="nx-report-period-control__preset" onClick={() => setShowCustom((v) => !v)}>
          {t("period.presets.custom")}
        </button>
      </div>
      {showCustom ? (
        <div className="nx-report-period-control__custom">
          <DateField
            label={t("period.from")}
            value={draftFrom}
            onChange={(value) => {
              setDraftFrom(value);
              if (singleDayOnly) setDraftTo(value);
            }}
            invalidFormatMessage={t("period.invalidFormat")}
            calendarButtonLabel={t("period.calendarButton")}
            previousMonthLabel={t("period.previousMonth")}
            nextMonthLabel={t("period.nextMonth")}
          />
          {singleDayOnly ? null : (
            <DateField
              label={t("period.to")}
              value={draftTo}
              onChange={setDraftTo}
              invalidFormatMessage={t("period.invalidFormat")}
              calendarButtonLabel={t("period.calendarButton")}
              previousMonthLabel={t("period.previousMonth")}
              nextMonthLabel={t("period.nextMonth")}
            />
          )}
          <button type="button" onClick={() => onChange({ from: draftFrom, to: singleDayOnly ? draftFrom : draftTo })}>
            {t("period.apply")}
          </button>
        </div>
      ) : null}
      <p className="nx-report-period-control__recap">{t("period.recap", { from: formatDateSr(period.from), to: formatDateSr(period.to) })}</p>
    </div>
  );
}
